import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { retry } from 'rxjs/operators';

import { ApiService } from './api.service';
import { DeveloperModeService } from './developer-mode.service';

export interface ApiCatalogOperation {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  bodyFormat?: 'json' | 'multipart';
  summary?: string;
}

/** Path de una operacion tal como lo publica el back, con los parametros entre llaves: '/records/{recordId}'. */
export type ApiOperationPath = `/${string}`;

interface ApiReferenceResponse {
  data: { attributes: { operations: Array<ApiCatalogOperation> } };
}

/**
 * Catalogo de operaciones publicas de la API (GET /api-reference), usado por el helper de campos en modo
 * desarrollador para saber con que endpoints puede enlazar cada formulario y con que forma (metodo, bodyFormat)
 * sin duplicar esa informacion a mano en el front.
 *
 * Los formularios declaran solo el path: un formulario siempre envia cuerpo, asi que su operacion es la unica de ese
 * path que lo acepta. Si el back publicara dos operaciones con cuerpo en el mismo path (p.ej. PUT y PATCH) no se
 * podria saber cual usa el formulario, y el path se trata como no encontrado. La visibilidad de cada endpoint la decide unicamente el back:
 * solo las operaciones publicas aparecen en la respuesta.
 *
 * El catalogo es igual para cualquier usuario y no cambia dentro de una sesion, asi que se pide una unica vez. La
 * peticion se lanza en la primera consulta con el modo desarrollador activo, no al construir el servicio: quien nunca
 * lo activa no la hace, y quien lo activa con un formulario ya abierto recibe el catalogo sin recrear la pantalla
 * (los helpers consultan en cada deteccion de cambios).
 *
 * Si la peticion falla, el catalogo queda vacio en vez de romper la pantalla: el helper no encuentra la operacion y
 * se oculta.
 */
@Injectable({ providedIn: 'root' })
export class ApiCatalogService {
  /**
   * Operacion con cuerpo de cada path, indexada para no recorrer el catalogo en cada deteccion de cambios. Null como
   * valor marca un path ambiguo; el mapa es null mientras el catalogo no haya llegado, sea porque aun se esta
   * pidiendo o porque la peticion acabo fallando.
   */
  private operations: Map<string, ApiCatalogOperation | null> | null = null;
  private requested: boolean = false;

  constructor(private http: HttpClient, private api: ApiService, private developerMode: DeveloperModeService) {}

  /** Operacion publica con cuerpo de ese path, o null si no existe, es ambigua o el catalogo aun no ha llegado. */
  find(path: ApiOperationPath): ApiCatalogOperation | null {
    if (!this.developerMode.enabled) return null;

    this.load();
    return this.operations?.get(path) ?? null;
  }

  private load(): void {
    if (this.requested) return;
    this.requested = true;

    this.http.get<ApiReferenceResponse>(`${this.api.endpoint}/api-reference`).pipe(
      // Un fallo transitorio (deploy en curso, blip de red) dejaria el modo desarrollador sin helpers hasta recargar
      // la pagina. Se reintenta un numero acotado de veces, con 1 s entre intentos: no se puede reintentar "en la
      // siguiente consulta" porque find() lo invoca el getter `show` de cada helper, o sea en cada deteccion de
      // cambios y por cada campo del formulario; con el back caido eso serian cientos de peticiones por segundo.
      // Agotados los intentos el catalogo se queda sin llegar: find() devuelve null y los helpers se ocultan, sin
      // reintentar ya nunca dentro de esta sesion (el flag `requested` no se revierte, a proposito).
      retry({ count: 3, delay: 1000 }),
    ).subscribe({
      next: result => {
        const operations = new Map<string, ApiCatalogOperation | null>();
        result.data.attributes.operations
          .filter(operation => operation.bodyFormat)
          .forEach(operation => operations.set(operation.path, operations.has(operation.path) ? null : operation));
        this.operations = operations;
      },
      // No hace falta asignar nada: `operations` sigue en null y find() lo trata igual que un mapa vacio
      // (`this.operations?.get(path) ?? null`). El que no se reintente lo garantiza `requested`, no esto.
      error: () => undefined,
    });
  }
}
